import type { FC } from "react";
import { isDevelopmentEnvironment } from "@/common/helpers/environment.helper";
import { DeviceOptionsScenarioPicker } from "@/simulation/device-options/components/ScenarioPicker/scenario-picker";
import {
  MockDeviceOptionsService,
  SCENARIO_QUERY_PARAM,
} from "@/simulation/device-options/mock-device-options-service";
import { HttpDeviceOptionsService } from "./device-options.http-adapter";
import type { DeviceOptionsService } from "./device-options-service";

export type DeviceOptionsServiceMode = "simulation" | "http";

export const resolveDeviceOptionsServiceMode = (
  hostname: string = window.location.hostname,
  search: string = window.location.search
): DeviceOptionsServiceMode =>
  isDevelopmentEnvironment(hostname) && new URLSearchParams(search).has(SCENARIO_QUERY_PARAM)
    ? "simulation"
    : "http";

const buildAdapter = (mode: DeviceOptionsServiceMode): DeviceOptionsService =>
  mode === "simulation" ? new MockDeviceOptionsService() : new HttpDeviceOptionsService();

const createModeSwitchingService = (): DeviceOptionsService => {
  const adapters = new Map<DeviceOptionsServiceMode, DeviceOptionsService>();
  const adapterFor = (mode: DeviceOptionsServiceMode): DeviceOptionsService => {
    const existing = adapters.get(mode);
    if (existing) return existing;
    const adapter = buildAdapter(mode);
    adapters.set(mode, adapter);
    return adapter;
  };
  return {
    getOptions: (params) => adapterFor(resolveDeviceOptionsServiceMode()).getOptions(params),
  };
};

export const createDeviceOptionsService = (mode?: DeviceOptionsServiceMode): DeviceOptionsService =>
  mode ? buildAdapter(mode) : createModeSwitchingService();

const NoDevTools: FC = () => null;

export const devToolsFor = (hostname: string): FC =>
  isDevelopmentEnvironment(hostname) ? DeviceOptionsScenarioPicker : NoDevTools;

export const DeviceOptionsDevTools: FC = devToolsFor(window.location.hostname);
